import { useState, useEffect, useRef } from 'react'
import { db } from '../lib/db'
import { searchCourse } from '../lib/searchCourse'
import { watchPosition } from '../lib/gps'
import ClubDetailModal from '../components/ClubDetailModal'

// Format a great-circle distance (miles, as returned by the nearest_courses
// action) as a compact "3.2 mi" label. Sub-10-mi gets one decimal; farther
// courses round to whole miles so the card stays tidy. Null/NaN → null so the
// card can omit the chip entirely.
export function fmtMiles(mi) {
  if (mi === null || mi === undefined || mi === '') return null
  const n = Number(mi)
  if (!Number.isFinite(n)) return null
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} mi`
}

// Turn a geolocation failure into one plain-English line for the card. Handles
// the browser GeolocationPositionError codes (1 = permission denied,
// 2 = position unavailable, 3 = timeout) plus the plain Error watchPosition
// throws when the platform has no geolocation at all.
export function geoErrMessage(err) {
  if (err && typeof err.code === 'number') {
    if (err.code === 1) return 'Location permission denied. Enable location access to see nearby courses.'
    if (err.code === 3) return 'Timed out getting your location. Try again outdoors or search above.'
    return 'Your location is unavailable right now. Search for a course above.'
  }
  return 'Location isn’t available on this device. Search for a course above.'
}

// Featured rows ({ id, name, city, state, is_club }) collapse the same way, but
// carry every member club id so the popup can union their courses.
function collapseFeatured(rows) {
  return mergeVenues(rows || [], (members) => ({
    id: members[0].id, name: venueName(members),
    city: members[0].city, state: members[0].state,
    is_club: true, memberIds: members.map((m) => m.id),
  }))
}

// Group clubs that resolve to the same real venue, then build a merged row from
// each group (single-member groups pass through untouched).
function mergeVenues(clubs, build) {
  const byVenue = new Map()
  for (const cl of clubs) {
    const k = venueKey(cl.name, cl.city, cl.state)
    if (!byVenue.has(k)) byVenue.set(k, [])
    byVenue.get(k).push(cl)
  }
  return [...byVenue.values()].map((members) => (members.length === 1 ? members[0] : build(members)))
}

// A venue key that ignores the per-course suffix ("… - The Retreat") and trailing
// generic golf words ("Golf", "Club", "Course", "Resort"), so the seed's
// inconsistent naming still collapses. Keyed on city+state to stay safe.
function venueKey(name, city, state) {
  let toks = (name || '').toLowerCase().split(/\s[-–—]\s/)[0].replace(/[&.,'']/g, ' ').split(/\s+/).filter(Boolean)
  const drop = new Set(['golf', 'club', 'course', 'courses', 'resort', 'links', 'gc', 'cc', 'the', 'and', 'at'])
  while (toks.length > 1 && drop.has(toks[toks.length - 1])) toks.pop()
  return `${toks.join(' ')}|${(city || '').toLowerCase()}|${(state || '').toLowerCase()}`
}

// Best label for a merged venue: a member with no course suffix (the club-level
// name), preferring a GolfAPI-backed one, longest wins; else the first member.
function venueName(members) {
  const noDash = members.filter((m) => !/\s[-–—]\s/.test(m.name || ''))
  const pool = noDash.length ? noDash : members
  return pool.slice().sort((a, b) =>
    (Number(!!b.hasGolfapi) - Number(!!a.hasGolfapi)) || ((b.name || '').length - (a.name || '').length))[0].name
}

export default function CoursesPage() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)   // grouped clubs from the search
  const [featured, setFeatured] = useState(null)  // most-played clubs (our DB)
  const [searching, setSearching] = useState(false)
  const [selectedClub, setSelectedClub] = useState(null) // opens the popup
  const [nearest, setNearest] = useState(null)     // 3 closest courses to the device
  const [geoError, setGeoError] = useState(null)   // permission-denied / unavailable message
  const debRef = useRef(null)

  // Featured = clubs with the most rounds played app-wide (our own data, free).
  useEffect(() => {
    db('get_featured_courses').then(({ data }) => setFeatured(collapseFeatured(data))).catch(() => setFeatured([]))
  }, [])

  // Nearest = the 3 courses closest to the device's current location. Reuse the
  // shared GPS singleton (gps.js watchPosition) and unsubscribe on the FIRST
  // fix — we only need one position, not a live track. On a permission-denied /
  // unavailable error, surface a plain message and let the user fall back to
  // search. Guard against setting state after unmount.
  useEffect(() => {
    let done = false
    let stop = () => {}
    stop = watchPosition(
      (fix) => {
        if (done) return
        done = true
        stop()
        db('nearest_courses', { lat: fix.lat, lng: fix.lng, limit: 3 })
          .then(({ data }) => setNearest(data || []))
          .catch(() => setNearest([]))
      },
      (err) => {
        if (done) return
        done = true
        stop()
        setGeoError(geoErrMessage(err))
        setNearest([])
      },
    )
    return () => { done = true; stop() }
  }, [])

  const onInput = (v) => {
    setQuery(v)
    if (debRef.current) clearTimeout(debRef.current)
    if (v.trim().length < 2) { setResults(null); setSearching(false); return }
    setSearching(true)
    debRef.current = setTimeout(async () => {
      try {
        const json = await searchCourse({ q: v.trim() })
        // Search now returns club rows (the venue index). Each is a club; the
        // popup pulls its courses on open (enrich-in-place).
        setResults((json.clubs || []).map((c) => ({ ...c, is_club: true })))
      } catch { setResults([]) }
      setSearching(false)
    }, 500)
  }

  const isSearch = query.trim().length >= 2

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: 20, paddingBottom: 100 }}>
        <h1 style={{ fontSize: '1.6rem', marginBottom: 14 }}>Courses</h1>

        <input
          className="input"
          value={query}
          onChange={(e) => onInput(e.target.value)}
          placeholder="Search courses..."
        />
        {searching && <p className="text-xs text-muted" style={{ marginTop: 6 }}>Searching…</p>}

        {/* NEAR YOU: 3 closest courses to the device location (hidden while searching) */}
        {!isSearch && (
          <>
            <p style={sectionLabelStyle}>Near you</p>
            {nearest === null && !geoError && <div className="spinner" style={{ margin: '18px auto' }} />}
            {geoError && <p className="text-muted text-sm">{geoError}</p>}
            {!geoError && nearest && nearest.length === 0 && (
              <p className="text-muted text-sm">No courses found near you yet.</p>
            )}
            {!geoError && nearest && nearest.length > 0 && (
              <div style={cardListStyle}>
                {nearest.map((c) => {
                  // Rows are now CLUB-sourced (Task L / D-343): `c.id` is a CLUB
                  // uuid and `c.course_id` is the club's enriched representative
                  // course (or null). Branch the tap target so the detail popup
                  // gets a shape its existing branches already handle:
                  //  • enriched club (course_id present) → pass the COURSE uuid as
                  //    `id`, NO is_club, so the modal auto-selects it and runs
                  //    detail-on-select — the same course-detail path as before.
                  //  • non-enriched club (no course_id) → pass the CLUB uuid as
                  //    `id` WITH is_club:true, driving get_club_courses /
                  //    enrich-on-demand club detail.
                  const target = c.course_id
                    ? { id: c.course_id, name: c.name, city: c.city, state: c.state }
                    : { id: c.id, name: c.name, city: c.city, state: c.state, is_club: true }
                  const statLine = [
                    c.holes != null ? `${c.holes} holes` : null,
                    c.course_rating != null ? `Rating ${c.course_rating}` : null,
                    c.slope_rating != null ? `Slope ${c.slope_rating}` : null,
                  ].filter(Boolean).join(' · ')
                  const place = [c.city, c.state].filter(Boolean).join(', ')
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedClub(target)}
                      style={nearTileStyle}
                    >
                      {/* Row 1: name (left) · distance (right) */}
                      <div style={nearTileRowStyle}>
                        <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                        {fmtMiles(c.distance) && (
                          <span className="text-muted" style={{ fontSize: '0.78rem', flexShrink: 0, marginLeft: 8 }}>{fmtMiles(c.distance)}</span>
                        )}
                      </div>
                      {/* Row 2: holes · rating · slope (left) · city, ST (right) */}
                      <div style={nearTileRowStyle}>
                        <span style={statRowStyle}>{statLine}</span>
                        {place && (
                          <span className="text-muted" style={{ fontSize: '0.78rem', flexShrink: 0, marginLeft: 8 }}>{place}</span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </>
        )}

        <p style={sectionLabelStyle}>
          {isSearch ? 'Results' : 'Featured'}
        </p>

        {/* SEARCH: clubs */}
        {isSearch && results && results.length > 0 && (
          <div style={listStyle}>
            {results.map((c, i) => (
              <button key={c.id || i} onClick={() => setSelectedClub(c)} style={rowStyle(i, results.length)}>
                <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                {(c.city || c.state) && <span className="text-muted" style={{ fontSize: '0.78rem', flexShrink: 0, marginLeft: 8 }}>{[c.city, c.state].filter(Boolean).join(', ')}</span>}
              </button>
            ))}
          </div>
        )}
        {isSearch && results && results.length === 0 && !searching && (
          <p className="text-muted text-sm">No courses found.</p>
        )}

        {/* FEATURED: most-played clubs from our DB */}
        {!isSearch && featured === null && <div className="spinner" style={{ margin: '24px auto' }} />}
        {!isSearch && featured && featured.length === 0 && (
          <p className="text-muted text-sm">No rounds logged yet — featured courses appear as people play.</p>
        )}
        {!isSearch && featured && featured.length > 0 && (
          <div style={listStyle}>
            {featured.map((c, i) => (
              <button key={c.id} onClick={() => setSelectedClub(c)} style={rowStyle(i, featured.length)}>
                <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                {(c.city || c.state) && (
                  <span className="text-muted" style={{ fontSize: '0.78rem', flexShrink: 0, marginLeft: 8 }}>
                    {[c.city, c.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedClub && <ClubDetailModal club={selectedClub} onClose={() => setSelectedClub(null)} />}
    </div>
  )
}

const sectionLabelStyle = { fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 10px' }
const cardListStyle = { display: 'flex', flexDirection: 'column', gap: 8 }
// Near-you tile: a clickable button styled like the Featured rows (rowStyle)
// but stacked into two rows.
const nearTileStyle = {
  width: '100%', textAlign: 'left', cursor: 'pointer',
  padding: '12px 14px',
  background: 'var(--green-dark)', color: 'var(--cream)',
  border: '1px solid var(--green-mid)', borderRadius: 10,
  display: 'flex', flexDirection: 'column', gap: 6,
}
const nearTileRowStyle = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }
const statRowStyle = {
  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  fontSize: '0.8rem', color: 'var(--gray-300)',
}
const listStyle = { display: 'flex', flexDirection: 'column', gap: 8 }
const rowStyle = () => ({
  width: '100%', textAlign: 'left', cursor: 'pointer',
  padding: '12px 14px',
  background: 'var(--green-dark)', color: 'var(--cream)',
  border: '1px solid var(--green-mid)', borderRadius: 10,
  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10,
})
